import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Validate a Brazilian CPF number.
 * Checks:
 * - Has exactly 11 digits
 * - Not all digits are the same
 * - Valid check digits
 *
 * @param cpf - The CPF string (formatted or unformatted)
 * @returns true if the CPF is valid, false otherwise
 */
export function validateCPF(cpf: string): boolean {
  // Remove tudo que não for dígito
  const digitsOnly = cpf.replace(/\D/g, '');

  // Verifica se tem 11 dígitos ou se é uma sequência de números iguais
  if (digitsOnly.length !== 11 || digitsOnly === digitsOnly[0].repeat(11)) {
    return false;
  }

  // Cálculo do primeiro dígito verificador (D1)
  // Multiplica os 9 primeiros dígitos por pesos de 10 a 2 e soma
  let somaD1 = 0;
  for (let i = 0; i < 9; i++) {
    somaD1 += parseInt(digitsOnly[i]) * (10 - i);
  }
  const d1 = somaD1 % 11 < 2 ? 0 : 11 - (somaD1 % 11);

  // Cálculo do segundo dígito verificador (D2)
  // Multiplica os 10 primeiros dígitos por pesos de 11 a 2 e soma
  let somaD2 = 0;
  for (let i = 0; i < 10; i++) {
    somaD2 += parseInt(digitsOnly[i]) * (11 - i);
  }
  const d2 = somaD2 % 11 < 2 ? 0 : 11 - (somaD2 % 11);

  // Retorna True apenas se os dois dígitos calculados baterem com os do CPF
  return digitsOnly[9] === String(d1) && digitsOnly[10] === String(d2);
}

/**
 * Parse a timestamp from the API into a JavaScript Date object.
 * Handles:
 * - Unix timestamps in seconds (from Go backend, e.g., 1768309937)
 * - ISO 8601 strings (e.g., "2025-01-14T12:00:00Z")
 *
 * @param value - The timestamp value from the API (number | string | Date)
 * @returns A Date object
 */
export function parseTimestamp(value: number | string | Date | null | undefined): Date {
  if (value == null) {
    return new Date();
  }

  if (value instanceof Date) {
    // Already a Date, return it
    return value;
  }

  if (typeof value === 'number') {
    // Seconds from Go backend - convert to milliseconds
    return new Date(value * 1000);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return new Date();
    }

    // Try parsing as ISO 8601 string
    const date = new Date(trimmed);
    if (!isNaN(date.getTime())) {
      return date;
    }

    // If it's a numeric string, try parsing as a number
    const num = parseFloat(trimmed);
    if (!isNaN(num)) {
      return new Date(num);
    }
  }

  // Fallback
  return new Date();
}

/**
 * Convert a Date to a Unix timestamp (seconds since epoch) for the API.
 * Matches Cosmos DB _ts format (e.g., 1768309937)
 * @param date - The Date to convert
 * @returns Unix timestamp in seconds
 */
export function toUnixTimestamp(date: Date): number {
  return Math.floor(date.getTime() / 1000);
}

/**
 * Format a timestamp for display.
 * Handles Unix timestamps (seconds), ISO 8601 strings, and Date objects.
 * @param date - The timestamp to format (number | string | Date)
 * @param locale - The locale for formatting (default: 'pt-BR')
 * @returns A formatted date string
 */
export function formatTimestamp(
  date: number | string | Date,
  locale: string = 'pt-BR'
): string {
  const parsed = parseTimestamp(date);
  return parsed.toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDate(date: Date | string | number): string {
  return formatTimestamp(date);
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

export function formatCPF(cpf: string): string {
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

export function formatCNPJ(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

export function formatCRM(value: string): string {
  // Remove special chars but keep letters and numbers
  const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
  
  // Remove "CRM" from the beginning if it exists, to simplify parsing
  const withoutCrm = cleaned.startsWith('CRM') ? cleaned.slice(3) : cleaned;
  
  // Match [UF][Numbers] or [Numbers][UF]
  // UF is exactly 2 letters
  const match = withoutCrm.match(/^([A-Z]{2})(\d+)$|^(\d+)([A-Z]{2})$/);
  
  if (match) {
    const uf = match[1] || match[4];
    const num = match[2] || match[3];
    return `CRM/${uf} ${num}`;
  }

  // If it's just numbers, don't auto-prefix yet, let user type UF
  // If it's just letters (besides CRM), it's probably the UF
  
  return cleaned.startsWith('CRM') ? cleaned.replace(/^CRM/, 'CRM/') : cleaned;
}

export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function getPercentageColor(percentage: number): string {
  if (percentage >= 90) return 'bg-error';
  if (percentage >= 75) return 'bg-warning';
  if (percentage >= 50) return 'bg-info';
  return 'bg-success';
}

export function getPercentageColorText(percentage: number): string {
  if (percentage >= 90) return 'text-error';
  if (percentage >= 75) return 'text-warning';
  if (percentage >= 50) return 'text-info';
  return 'text-success';
}

/**
 * Debounce function for delaying function execution
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Format a date to relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: Date | string | number): string {
  const parsed = parseTimestamp(date);
  const now = new Date();
  const diffMs = now.getTime() - parsed.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'agora';
  if (diffMins < 60) return `${diffMins} min atrás`;
  if (diffHours < 24) return `${diffHours}h atrás`;
  if (diffDays < 7) return `${diffDays}d atrás`;
  return formatTimestamp(parsed);
}
